import { useRef, useState } from 'react';

import { useMediaQuery, useResizeObserver } from '../../hooks';
import { combineAttributeStrings, useDefaultPropsFromTheme } from '../../utils/internal';
import { Cell } from '../cell';

import { heroHeaderId, heroImageId, heroSubheaderId } from './constants';
import { HeroComplementaryCta, HeroPrimaryCta } from './hero-cta';
import { HeroTerms } from './hero-terms';
import {
  ButtonsWrapper,
  HeroBackground,
  HeroContainer,
  HeroGrid,
  HeroHeader,
  HeroSubheader,
  ImageWrapper,
} from './hero.styled';
import { HeroProps } from './types';

/**
 * the default area at the top of the home page
 */
const Hero: React.FC<HeroProps> = ({
  header,
  subheader,
  image,
  reversed = false,
  disableButtonReverse,
  primaryCtaText,
  primaryCtaHref,
  primaryCtaAs,
  onClickPrimaryCta,
  complementaryCtaText,
  complementaryCtaHref,
  complementaryCtaAs,
  onClickComplementaryCta,
  termsLinkText,
  onClickTermsLink,
  termsLinkHref,
  termsLinkAs,
  showComplementaryCta = true,
  background,
  backgroundSplit = false,
}) => {
  const isMobile = !useMediaQuery('tablet');

  const heroTermsId = termsLinkText ? 'hero-terms-wrapper' : undefined;

  const ctaDescribedByIds = combineAttributeStrings(
    heroImageId,
    heroHeaderId,
    heroSubheaderId,
    heroTermsId,
  );

  const { disableButtonReverse: isButtonReverseDisabled } = useDefaultPropsFromTheme('Hero', {
    disableButtonReverse,
  });

  const isButtonReversed = reversed && !isButtonReverseDisabled && !backgroundSplit;

  const hasComplementaryCta = showComplementaryCta && complementaryCtaText;

  const isTermsReversed = reversed && !backgroundSplit;

  const [splitHeight, setSplitHeight] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);

  const subheaderRef = useRef<HTMLDivElement>(null);
  const imageWrapperRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);

  useResizeObserver(subheaderRef, () => setSplitHeight(subheaderRef.current?.offsetTop ?? 0));
  useResizeObserver(imageRef, () =>
    setImageHeight(imageRef.current ? imageRef.current.offsetTop / 1.5 : 0),
  );
  useResizeObserver(imageWrapperRef, () => {
    setSplitHeight(subheaderRef.current?.offsetTop ?? 0);
  });

  return (
    <HeroContainer>
      <HeroBackground
        data-testid="hero-background-test-id"
        $background={background}
        $backgroundSplit={backgroundSplit}
        $backgroundSplitHeight={isMobile ? imageHeight : splitHeight}
      />
      <section style={{ position: 'relative', overflow: 'hidden' }}>
        <HeroGrid $reversed={reversed && (!isMobile || !backgroundSplit)}>
          <Cell startTablet={5} span={2} spanTablet={4}>
            <ImageWrapper ref={imageWrapperRef} id={heroImageId}>
              {image}
            </ImageWrapper>
            <div ref={imageRef} />
          </Cell>

          <Cell
            rowTablet={1}
            span={2}
            startTablet={1}
            spanTablet={4}
            spanDesktopLarge={5}
            startDesktopLarge={2}
          >
            <HeroHeader id={heroHeaderId}>{header}</HeroHeader>

            {subheader && <HeroSubheader id={heroSubheaderId}>{subheader}</HeroSubheader>}

            <div ref={subheaderRef} />

            <ButtonsWrapper>
              <HeroPrimaryCta
                aria-describedby={ctaDescribedByIds}
                fullWidth={!hasComplementaryCta || isMobile}
                reversed={isButtonReversed}
                children={primaryCtaText}
                onClick={onClickPrimaryCta}
                href={primaryCtaHref}
                as={primaryCtaAs}
              />

              {hasComplementaryCta && (
                <HeroComplementaryCta
                  aria-describedby={ctaDescribedByIds}
                  fullWidth={isMobile}
                  reversed={isButtonReversed}
                  children={complementaryCtaText}
                  onClick={onClickComplementaryCta}
                  href={complementaryCtaHref}
                  as={complementaryCtaAs}
                />
              )}
            </ButtonsWrapper>
          </Cell>
        </HeroGrid>

        {termsLinkText ? (
          <HeroTerms
            id={heroTermsId}
            onClickTermsLink={onClickTermsLink}
            reversed={isTermsReversed}
            termsLinkText={termsLinkText}
            termsLinkAs={termsLinkAs}
            termsLinkHref={termsLinkHref}
          />
        ) : null}
      </section>
    </HeroContainer>
  );
};

export default Hero;
